import { severityPresentation } from '../../lib/detections/semantics.ts'
import type { ThreatBucket } from '../../lib/detections/overview.ts'
import { formatAxisTime, formatDayMonth } from '../../lib/format.ts'
import type { DetectionSeverity } from '../../lib/sse/types.ts'
import { useElementSize } from '../charts/useElementSize.ts'

const FILL: Record<DetectionSeverity, string> = {
  critical: 'fill-crit',
  high: 'fill-high',
  medium: 'fill-warn',
  low: 'fill-accent',
}

/** De baixo para cima: a crítica fica no topo da pilha, onde o olho chega. */
const STACK = ['low', 'medium', 'high', 'critical'] as const

const HEIGHT = 140
const AXIS = 18

/**
 * Ameaças por balde (hora ou dia), empilhadas por severidade. Baldes vazios
 * ficam no eixo: "dia sem ameaça" é informação, não buraco.
 */
export function ThreatChart({ buckets, hourly }: { buckets: readonly ThreatBucket[]; hourly: boolean }) {
  const [ref, size] = useElementSize<HTMLDivElement>()
  const totals = buckets.map((b) => b.critical + b.high + b.medium + b.low + b.other)
  const peak = Math.max(1, ...totals)
  // Um rótulo a cada ~70 px, para nunca encavalar.
  const every = Math.max(1, Math.ceil((buckets.length * 70) / Math.max(1, size.width)))
  const label = (start: number) =>
    hourly ? `${formatAxisTime(new Date(start)).slice(0, 2)}h` : formatDayMonth(new Date(start))

  return (
    <div ref={ref} className="w-full">
      {size.width > 0 && (
        <svg width={size.width} height={HEIGHT + AXIS} role="img" aria-label="Ameaças detectadas ao longo do tempo">
          {[0.5, 1].map((f) => (
            <g key={f}>
              <line
                x1={0}
                x2={size.width}
                y1={HEIGHT - (HEIGHT - 8) * f}
                y2={HEIGHT - (HEIGHT - 8) * f}
                className="stroke-grid"
                strokeDasharray="2 3"
              />
              <text x={2} y={HEIGHT - (HEIGHT - 8) * f + 11} className="fill-fg-muted font-mono text-micro">
                {Math.round(peak * f)}
              </text>
            </g>
          ))}
          {buckets.map((bucket, i) => {
            const w = size.width / buckets.length
            const gap = w > 12 ? 3 : 1
            let y = HEIGHT
            return (
              <g key={bucket.start}>
                <title>{`${hourly ? formatAxisTime(new Date(bucket.start)) : formatDayMonth(new Date(bucket.start))} UTC · ${totals[i]} ameaça(s)`}</title>
                <rect x={i * w} y={0} width={w} height={HEIGHT} className="fill-transparent" />
                {STACK.map((severity) => {
                  const count = bucket[severity]
                  if (count === 0) return null
                  const h = (count / peak) * (HEIGHT - 8)
                  y -= h
                  return (
                    <rect
                      key={severity}
                      x={i * w + gap / 2}
                      y={y}
                      width={Math.max(1, w - gap)}
                      height={h}
                      className={FILL[severity]}
                    >
                      <title>{`${severityPresentation(severity).label}: ${count}`}</title>
                    </rect>
                  )
                })}
                {bucket.other > 0 && (
                  <rect
                    x={i * w + gap / 2}
                    y={y - (bucket.other / peak) * (HEIGHT - 8)}
                    width={Math.max(1, w - gap)}
                    height={(bucket.other / peak) * (HEIGHT - 8)}
                    className="fill-line-strong"
                  />
                )}
                {i % every === 0 && (
                  <text
                    x={i * w + w / 2}
                    y={HEIGHT + 13}
                    textAnchor="middle"
                    className="fill-fg-muted font-mono text-micro"
                  >
                    {label(bucket.start)}
                  </text>
                )}
              </g>
            )
          })}
          <line x1={0} x2={size.width} y1={HEIGHT} y2={HEIGHT} className="stroke-line" />
        </svg>
      )}
      {size.width === 0 && <div style={{ height: HEIGHT + AXIS }} />}
    </div>
  )
}
